import { imagePath } from '../../../../utils/imagePath';

const IntroductionData = {
  slug: 'introduction',
  sectionTitle: 'Introduction',
  blocks: [
    {
      type: 'text',
      content: [
        'Spatial Grid is an enterprise-grade spatial computing platform designed to build, deploy, and scale immersive AR, VR, simulator and MR experiences across industries. This section introduces the platforms philosophy, core capabilities, and how enterprises use Spatial Grid to drive training, visualization, and engagement.',
        'Administrators can start using the latest Spatial Grid tools by managing feature access directly from admin settings, ensuring controlled, secure, and scalable access across teams while transforming ideas into engaging digital experiences that blend the physical and virtual worlds.',
      ],
    },
  ],
  subSections: [
    {
      title: '1. What the Spatial Grid Website Helps You Understand',
      blocks: [
        {
          type: 'text',
          content:
            'The Spatial Grid website is designed to give visitors a **clear understanding of the platform, its capabilities, and its value,** before they begin creating.',
        },
        {
          type: 'text',
          content: 'Through the website, users can:',
        },
        {
          type: 'list',
          items: [
            'Understand what AR, VR, and MR experiences are and how they differ',
            'Explore how Spatial Grid supports immersive creation and animation',
            'Learn how teams collaborate and manage projects at scale',
            'Discover how advanced tools like AI-assisted creation and walkthroughs enhance productivity',
            'See how the platform adapts to different industries and use cases',
          ],
        },
      ],
    },
    {
      title: '2. Immersive Experience Creation',
      blocks: [
        {
          type: 'text',
          content:
            'Spatial Grid enables the creation of interactive spatial experiences, including:',
        },
        {
          type: 'list',
          items: [
            'Augmented Reality overlays in real-world environments',
            'Fully immersive Virtual Reality simulations',
            'Mixed Reality experiences where digital content interacts with physical space',
            '3D animations with motion, lighting, and effects',
          ],
        },
        {
          type: 'info',
          content:
            'These capabilities are presented visually and conceptually on the website to help users imagine whats possible.',
        },
      ],
    },
    {
      title: '3. Accessing the Platform',
      blocks: [
        {
          type: 'text',
          content: [
            'Once users are ready to begin, the website provides secure access to the Spatial Grid platform. After authentication, users enter their workspace environment, where their projects, tools, and permissions are already configured based on their role and organization.',
            'This transition from website to platform ensures continuity—from understanding the product to actively using it.',
          ],
        },
        {
          type: 'image',
          src: imagePath.AccessPlatform,
          alt: 'Dashboard',
        },
        {
          type: 'text',
          content: [
            'The Spatial Grid login page is your secure entry point to the immersive creation platform. From here, you can sign in or register to access your organizations workspace and begin working on AR, VR, and Mixed Reality experiences.',
          ],
        },
      ],
    },
    {
      title: '4. Secure, Organization-Based Access',
      blocks: [
        {
          type: 'text',
          content: [
            'Spatial Grid uses secure, role-based access to protect your work:',
          ],
        },
        {
          type: 'list',
          items: [
            'Login access is tied to your organization',
            'Features are enabled based on admin settings',
            'All access activity can be managed and audited',
          ],
        },
        {
          type: 'text',
          content: [
            'This allows teams to collaborate confidently while maintaining control.',
          ],
        },
      ],
    },
    {
      title: '5. Understanding the Spatial Grid Dashboard',
      blocks: [
        {
          type: 'text',
          content: [
            'After access is granted, users arrive at a central dashboard that acts as the foundation for all activity within the Spatial Grid. The dashboard is designed to:',
          ],
        },
        {
          type: 'list',
          items: [
            'Present an overview of projects and immersive experiences',
            'Provide quick access to AR, VR, and MR creation workflows',
            'Surface available tools, features, and assets',
            'Reflect organizational settings and feature availability',
            'Support both individual creators and collaborative teams',
          ],
        },
        {
          type: 'text',
          content: [
            'Rather than being just a control panel, the dashboard connects **creation, management, and collaboration** in one place.',
          ],
        },
        {
          type: 'image',
          src: imagePath?.SpatialgridDashboard,
          alt: 'Dashboard',
        },
      ],
    },
  ],
};
export default IntroductionData;
